import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    FaSearch,
    FaSortAlphaDown,
    FaSortAlphaUpAlt
} from 'react-icons/fa/index.js';
import { userHasLeadershipAdminRole } from '../../utils/user-leadership.js';

const LEADERSHIP_HD_TEAM = 'Leadership';

function teamsMatch(a, b) {
    const x = String(a || '')
        .trim()
        .toLowerCase();
    const y = String(b || '')
        .trim()
        .toLowerCase();
    return x.length > 0 && x === y;
}

/**
 * Admin users appear under their hdTeam and under Leadership (matches Oyster roster).
 */
const enrichSidebarWithLeadership = (data) => {
    if (!data?.length) return [];
    const leadIdx = data.findIndex((g) =>
        teamsMatch(g.team, LEADERSHIP_HD_TEAM)
    );

    let leadershipUsers = leadIdx >= 0 ? [...(data[leadIdx].users || [])] : [];

    const seen = new Set(
        leadershipUsers.map((u) =>
            String(u.email || '')
                .trim()
                .toLowerCase()
        )
    );

    data.forEach((g) => {
        if (teamsMatch(g.team, LEADERSHIP_HD_TEAM)) return;
        (g.users || []).forEach((u) => {
            if (!userHasLeadershipAdminRole(u)) return;
            const em = String(u.email || '')
                .trim()
                .toLowerCase();
            if (!em || seen.has(em)) return;
            seen.add(em);
            leadershipUsers.push(u);
        });
    });

    leadershipUsers = leadershipUsers.sort((a, b) => {
        const nameA = `${a.firstName || ''} ${a.lastName || ''}`;
        const nameB = `${b.firstName || ''} ${b.lastName || ''}`;
        return nameA.localeCompare(nameB);
    });

    if (leadIdx >= 0) {
        return data.map((g, i) =>
            i === leadIdx ? { ...g, users: leadershipUsers } : g
        );
    }

    if (leadershipUsers.length === 0) return data;

    return [...data, { team: LEADERSHIP_HD_TEAM, users: leadershipUsers }];
};

const Sidebar = ({ sidebarData, isLoading, selectedView, setSelectedView }) => {
    const [isUnassignedExpanded, setIsUnassignedExpanded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortAsc, setSortAsc] = useState(true);

    const enrichedSidebarData = useMemo(
        () => enrichSidebarWithLeadership(sidebarData),
        [sidebarData]
    );

    const filteredAndSortedData = useMemo(() => {
        if (!enrichedSidebarData) return [];
        const q = searchQuery.toLowerCase().trim();

        // Filter: keep teams that match by name or have matching users
        let filtered = enrichedSidebarData
            .map((teamGroup) => {
                const teamName = (teamGroup.team || '').toLowerCase();
                const users = teamGroup.users || [];

                if (!q) return teamGroup;

                const teamMatches = teamName.includes(q);
                const matchingUsers = users.filter((u) => {
                    const fullName = `${u.firstName || ''} ${
                        u.lastName || ''
                    }`.toLowerCase();
                    return (
                        fullName.includes(q) ||
                        (u.email || '').toLowerCase().includes(q)
                    );
                });

                // If team name matches, show all users; if users match, show only those
                if (teamMatches) return teamGroup;
                if (matchingUsers.length > 0) {
                    return { ...teamGroup, users: matchingUsers };
                }
                return null;
            })
            .filter(Boolean);

        // Sort teams
        filtered.sort((a, b) => {
            const teamA = a.team || '';
            const teamB = b.team || '';
            if (teamA.toLowerCase() === 'unassigned') return 1;
            if (teamB.toLowerCase() === 'unassigned') return -1;
            return sortAsc
                ? teamA.localeCompare(teamB)
                : teamB.localeCompare(teamA);
        });

        // Sort users within each team
        filtered = filtered.map((teamGroup) => ({
            ...teamGroup,
            users: [...(teamGroup.users || [])].sort((a, b) => {
                const nameA = `${a.firstName || ''} ${a.lastName || ''}`;
                const nameB = `${b.firstName || ''} ${b.lastName || ''}`;
                return sortAsc
                    ? nameA.localeCompare(nameB)
                    : nameB.localeCompare(nameA);
            })
        }));

        return filtered;
    }, [enrichedSidebarData, searchQuery, sortAsc]);

    // Auto-expand teams when searching
    const hasSearch = searchQuery.trim().length > 0;

    return (
        <aside className="team-tasks-sidebar">
            {/* Search & Sort Controls */}
            <div className="sidebar-controls">
                <div className="sidebar-search">
                    <FaSearch className="sidebar-search-icon" />
                    <input
                        type="text"
                        className="sidebar-search-input"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <button
                    className="sidebar-sort-btn"
                    onClick={() => setSortAsc(!sortAsc)}
                    title={sortAsc ? 'Sort Z-A' : 'Sort A-Z'}>
                    {sortAsc ? <FaSortAlphaDown /> : <FaSortAlphaUpAlt />}
                </button>
            </div>

            <nav className="sidebar-nav">
                {isLoading ? (
                    <div
                        style={{
                            padding: '2rem',
                            textAlign: 'center',
                            color: '#6b7280',
                            fontSize: '0.875rem'
                        }}>
                        Loading teams...
                    </div>
                ) : (
                    <>
                        {/* 1. All Teams Level */}
                        <div
                            className={`sidebar-item all-teams-item ${
                                selectedView.type === 'all' ? 'active' : ''
                            }`}
                            onClick={() => setSelectedView({ type: 'all' })}>
                            <span className="item-label">All Teams</span>
                        </div>

                        {/* 2. Render Teams List */}
                        <div className="team-accordion-list">
                            {filteredAndSortedData.length === 0 ? (
                                <div
                                    style={{
                                        padding: '1rem 1.5rem',
                                        color: '#9ca3af',
                                        fontSize: '0.85rem'
                                    }}>
                                    {hasSearch
                                        ? 'No matching users'
                                        : 'No teams found'}
                                </div>
                            ) : (
                                filteredAndSortedData.map((teamGroup) => {
                                    const teamName =
                                        teamGroup.team || 'Unknown';

                                    const isTeamSelected =
                                        selectedView.type === 'team' &&
                                        selectedView.id === teamName;

                                    const isUnassigned =
                                        teamName.toLowerCase() === 'unassigned';
                                    // Auto-expand when searching, otherwise normal behavior
                                    const isExpanded = hasSearch
                                        ? true
                                        : !isUnassigned || isUnassignedExpanded;

                                    return (
                                        <div
                                            key={teamName}
                                            className="team-accordion-group">
                                            {/* Team Header Level */}
                                            <div
                                                className={`team-accordion-header ${
                                                    isTeamSelected
                                                        ? 'active'
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    setSelectedView({
                                                        type: 'team',
                                                        id: teamName
                                                    })
                                                }>
                                                <span className="item-label clickable bold-team">
                                                    {teamName}
                                                </span>
                                                {isUnassigned && !hasSearch && (
                                                    <button
                                                        className="btn-expander"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setIsUnassignedExpanded(
                                                                !isUnassignedExpanded
                                                            );
                                                        }}>
                                                        {isUnassignedExpanded
                                                            ? '▼'
                                                            : '▶'}
                                                    </button>
                                                )}
                                            </div>

                                            {/* 3. User Level Items */}
                                            {isExpanded && (
                                                <div className="team-accordion-body always-expanded">
                                                    {(
                                                        teamGroup.users || []
                                                    ).map((user) => {
                                                        const isUserSelected =
                                                            selectedView.type ===
                                                                'user' &&
                                                            selectedView.email ===
                                                                user.email;
                                                        return (
                                                            <div
                                                                key={`${teamName}-${user.email}`}
                                                                className={`sidebar-item user-item ${
                                                                    isUserSelected
                                                                        ? 'active'
                                                                        : ''
                                                                }`}
                                                                onClick={() =>
                                                                    setSelectedView(
                                                                        {
                                                                            type: 'user',
                                                                            email: user.email,
                                                                            name: `${user.firstName} ${user.lastName}`
                                                                        }
                                                                    )
                                                                }>
                                                                {user.firstName}{' '}
                                                                {user.lastName}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </>
                )}
            </nav>
        </aside>
    );
};

Sidebar.propTypes = {
    sidebarData: PropTypes.array.isRequired,
    isLoading: PropTypes.bool,
    selectedView: PropTypes.object.isRequired,
    setSelectedView: PropTypes.func.isRequired,
    currentUserTeam: PropTypes.string
};

export default Sidebar;
